import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, LogIn, LogOut, Plus,
  Umbrella, Stethoscope, Sun, Clock3, CheckCircle2, XCircle, Loader2, Inbox,
} from "lucide-react";
import { CalendarGridSkeleton } from "@/components/LoadingSkeleton";
import {
  toISODate, isWeekend, monthKey, buildMonthGrid, monthLabel,
  WEEKDAY_HEADS, LEAVE_LABELS, LEAVE_SHORT,
} from "@/lib/attendance";
import Payroll from "@/pages/Payroll";

const LEAVE_STYLE = {
  CL: { chip: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500", icon: Umbrella },
  ML: { chip: "bg-sky-100 text-sky-800 border-sky-200", dot: "bg-sky-500", icon: Stethoscope },
  half_day: { chip: "bg-violet-100 text-violet-800 border-violet-200", dot: "bg-violet-500", icon: Clock3 },
};

function StatusRibbon({ status }) {
  if (status === "approved") {
    return (
      <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-500 text-white px-1.5 py-0.5 rounded shadow-sm">
        <CheckCircle2 className="h-2.5 w-2.5" /> Approved
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-red-500 text-white px-1.5 py-0.5 rounded shadow-sm">
        <XCircle className="h-2.5 w-2.5" /> Rejected
      </span>
    );
  }
  if (status === "cancelled") {
    return (
      <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-slate-400 text-white px-1.5 py-0.5 rounded shadow-sm">
        <XCircle className="h-2.5 w-2.5" /> Cancelled
      </span>
    );
  }
  return (
    <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded shadow-sm">
      <Clock3 className="h-2.5 w-2.5" /> Pending
    </span>
  );
}

function DayCell({ date, outside, entry, leaves, isToday, onOpenAction, isFuture }) {
  const iso = toISODate(date);
  const weekend = isWeekend(date);
  const disabled = outside || weekend;
  const dayLabel = date.getDate();

  // Card style
  let bg = "bg-white";
  let border = "border-slate-200";
  if (weekend) { bg = "bg-slate-50"; border = "border-slate-100"; }
  if (outside) { bg = "bg-slate-50/50"; border = "border-slate-100"; }

  const activeLeaves = leaves.filter((l) => l.status !== "cancelled");
  const leaveApproved = activeLeaves.find((l) => l.status === "approved");
  const leaveRejected = activeLeaves.find((l) => l.status === "rejected");
  const leavePending  = activeLeaves.find((l) => l.status === "pending");
  const primaryLeave  = leaveApproved || leavePending || leaveRejected;

  if (leaveApproved) { bg = "bg-emerald-50/70"; border = "border-emerald-300"; }
  else if (leaveRejected) { bg = "bg-red-50/70"; border = "border-red-300"; }
  else if (leavePending) { bg = "bg-amber-50/70"; border = "border-amber-300"; }
  else if (entry?.holiday) { bg = "bg-purple-50/70"; border = "border-purple-300"; }

  return (
    <div
      data-testid={`att-cell-${iso}`}
      className={`relative min-h-[110px] rounded-lg border ${border} ${bg} p-1.5 transition ${disabled ? "opacity-70" : "hover:shadow-md hover:border-purple-200"}`}
    >
      <div className="flex items-center justify-between">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${isToday ? "bg-purple-600 text-white" : outside ? "text-slate-300" : "text-slate-600"}`}>
          {dayLabel}
        </span>
        {weekend && !outside && <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Off</span>}
        {!disabled && (
          <button
            onClick={() => onOpenAction(date)}
            data-testid={`att-add-${iso}`}
            title={isFuture ? "Apply for future leave" : "Add attendance / leave"}
            className="p-1 rounded-full text-slate-400 hover:bg-purple-100 hover:text-purple-700 transition"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {primaryLeave && <StatusRibbon status={primaryLeave.status} />}

      <div className="mt-1 space-y-1">
        {entry?.holiday && (
          <div className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-purple-200 text-purple-900" title={entry.holiday_reason || "Holiday"}>
            <Sun className="h-2.5 w-2.5" /> Holiday
          </div>
        )}
        {entry?.login_time && (
          <div className="flex items-center gap-1 text-[10px] text-emerald-700">
            <LogIn className="h-2.5 w-2.5" /> {entry.login_time}
          </div>
        )}
        {entry?.logout_time && (
          <div className="flex items-center gap-1 text-[10px] text-rose-700">
            <LogOut className="h-2.5 w-2.5" /> {entry.logout_time}
          </div>
        )}
        {leaves.map((lv) => {
          const cfg = LEAVE_STYLE[lv.leave_type] || LEAVE_STYLE.CL;
          const Icon = cfg.icon;
          const isCancelled = lv.status === "cancelled";
          return (
            <div key={lv.id} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${cfg.chip} ${isCancelled ? "opacity-50 line-through" : ""}`} title={`${LEAVE_LABELS[lv.leave_type]}${isCancelled ? " (cancelled)" : ""}${lv.reason ? " — " + lv.reason : ""}`}>
              <Icon className="h-2.5 w-2.5" /> {LEAVE_SHORT[lv.leave_type]}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActionDialog({ open, onClose, date, isFuture, onSubmit }) {
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setAction(null); setReason(""); }
  }, [open]);

  const iso = date ? toISODate(date) : "";
  const label = date ? date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";

  /* Iter 45.37 — Login / Logout auto-capture the wall-clock time the
     moment the user clicks the option, so marking attendance is truly
     one click.  No manual time-picker step. */
  const quickStamp = async (kind) => {
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    setSaving(true);
    try {
      await onSubmit({
        kind: "entry",
        date: iso,
        patch: kind === "login" ? { login_time: hhmm } : { logout_time: hhmm },
      });
      onClose();
    } catch (e) {
      // toast handled upstream by parent's onSubmit
    } finally {
      setSaving(false);
    }
  };

  const options = [
    { key: "login",   label: "Login",        icon: LogIn,       hint: "Auto-captures current time",                    disabled: isFuture, kind: "quick" },
    { key: "logout",  label: "Logout",       icon: LogOut,      hint: "Auto-captures current time",                    disabled: isFuture, kind: "quick" },
    { key: "CL",      label: "Apply for CL", icon: Umbrella,    hint: "Casual leave (upper hierarchy approves)",       disabled: false,     kind: "leave-reason" },
    { key: "ML",      label: "Apply for ML", icon: Stethoscope, hint: "Medical leave (past dates allowed)",            disabled: false,     kind: "leave-reason" },
    { key: "half_day",label: "Apply Half Day",icon: Clock3,     hint: "Half day (upper hierarchy approves)",           disabled: isFuture, kind: "leave-reason" },
    { key: "holiday", label: "Mark Holiday", icon: Sun,         hint: "Add a note describing the holiday",             disabled: isFuture, kind: "reason" },
  ];

  const commit = async () => {
    if (!action) return;
    setSaving(true);
    try {
      if (action === "holiday") {
        await onSubmit({ kind: "entry", date: iso, patch: { holiday: true, holiday_reason: reason } });
      } else {
        await onSubmit({ kind: "leave", date: iso, leave_type: action, reason });
      }
      onClose();
    } catch (e) {
      // handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="att-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarIcon className="h-4 w-4 text-purple-500" /> {label}</DialogTitle>
          <DialogDescription>Choose an action for this day.</DialogDescription>
        </DialogHeader>

        {!action ? (
          <div className="grid grid-cols-2 gap-2">
            {options.map((o) => {
              const Icon = o.icon;
              return (
                <button
                  key={o.key}
                  disabled={o.disabled || saving}
                  onClick={() => (o.key === "login" || o.key === "logout") ? quickStamp(o.key) : setAction(o.key)}
                  data-testid={`att-opt-${o.key}`}
                  className={`text-left rounded-lg border p-3 transition ${o.disabled ? "opacity-40 cursor-not-allowed bg-slate-50" : "hover:border-purple-400 hover:bg-purple-50/40 border-slate-200 bg-white"}`}
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="h-4 w-4 text-purple-500" /> {o.label}</div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{o.hint}</p>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-slate-800">{options.find(o => o.key === action)?.label}</div>
            {(action === "holiday" || action === "CL" || action === "ML" || action === "half_day") && (
              <div>
                <Label className="text-xs">{action === "holiday" ? "Reason for holiday" : "Reason (optional)"}</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={action === "holiday" ? "e.g., Company off, National holiday…" : "e.g., Personal work, appointment…"} data-testid="att-reason-input" />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {action && <Button variant="ghost" onClick={() => setAction(null)} disabled={saving} data-testid="att-back-btn">← Back</Button>}
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="att-cancel-btn">Cancel</Button>
          {action && (
            <Button onClick={commit} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="att-submit-btn">
              {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</> : "Submit"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalInbox({ items, users, onDecide }) {
  const [busy, setBusy] = useState({});
  const [noteFor, setNoteFor] = useState(null); // { id, decision }
  const [note, setNote] = useState("");
  if (!items.length) return null;

  const submit = async (id, decision, n) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try { await onDecide(id, decision, n || ""); } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  return (
    <div className="bg-white rounded-lg border border-amber-200 p-4 space-y-3" data-testid="approval-inbox">
      <div className="flex items-center gap-2">
        <Inbox className="h-4 w-4 text-amber-600" />
        <h3 className="text-sm font-semibold text-slate-800">Pending your approval</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{items.length}</span>
      </div>
      <div className="divide-y divide-slate-100 -mx-2">
        {items.map((lv) => {
          const cfg = LEAVE_STYLE[lv.leave_type] || LEAVE_STYLE.CL;
          const Icon = cfg.icon;
          return (
            <div key={lv.id} className="flex items-start gap-3 px-2 py-2.5" data-testid={`inbox-row-${lv.id}`}>
              <span className={`mt-0.5 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${cfg.chip}`}>
                <Icon className="h-3 w-3" /> {LEAVE_LABELS[lv.leave_type]}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800">
                  <span className="font-semibold">{lv.user_name}</span> — <span className="text-slate-600">{lv.date}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{lv.reason || <em className="opacity-60">No reason provided</em>}</div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" onClick={() => submit(lv.id, "approved")} disabled={busy[lv.id]} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`inbox-approve-${lv.id}`}>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setNoteFor({ id: lv.id, decision: "rejected" }); setNote(""); }} disabled={busy[lv.id]} className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid={`inbox-reject-${lv.id}`}>
                  <XCircle className="h-3 w-3 mr-1" /> Reject
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!noteFor} onOpenChange={(o) => !o && setNoteFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reason for rejection</DialogTitle>
            <DialogDescription>Optional note visible to the requester.</DialogDescription>
          </DialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain why…" data-testid="reject-note-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteFor(null)} data-testid="reject-note-cancel">Cancel</Button>
            <Button onClick={async () => { const id = noteFor.id; setNoteFor(null); await submit(id, "rejected", note); }} className="bg-red-600 hover:bg-red-700" data-testid="reject-note-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Attendance({ embedded = false }) {
  const { user } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [dialogDate, setDialogDate] = useState(null);

  const mk = monthKey(cursor);
  const minMonthDate = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    return d;
  }, []); // eslint-disable-line
  const maxMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), []); // eslint-disable-line

  const canGoPrev = cursor > minMonthDate;
  const canGoNext = cursor < maxMonthDate;

  const load = async () => {
    setLoading(true);
    try {
      const [me, ibx] = await Promise.all([
        api.get(`/attendance/me?month=${mk}`),
        api.get(`/attendance/leave/inbox`).catch(() => ({ data: [] })),
      ]);
      setEntries(me.data.entries || []);
      setLeaves(me.data.leaves || []);
      setInbox(ibx.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [mk]); // eslint-disable-line

  const entryByDate = useMemo(() => Object.fromEntries(entries.map((e) => [e.date, e])), [entries]);
  const leavesByDate = useMemo(() => {
    const map = {};
    for (const lv of leaves) { (map[lv.date] = map[lv.date] || []).push(lv); }
    return map;
  }, [leaves]);

  const grid = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());

  const submitAction = async (payload) => {
    try {
      if (payload.kind === "entry") {
        await api.post("/attendance/entry", { date: payload.date, ...payload.patch });
        toast.success("Attendance saved");
      } else {
        await api.post("/attendance/leave", { leave_type: payload.leave_type, date: payload.date, reason: payload.reason });
        toast.success("Leave request submitted");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const decide = async (lid, decision, note) => {
    try {
      await api.post(`/attendance/leave/${lid}/decide`, { decision, note });
      toast.success(`Leave ${decision}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelLeave = async (lid) => {
    try {
      await api.post(`/attendance/leave/${lid}/cancel`);
      toast.success("Leave cancelled");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shift = (delta) => {
    const next = new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1);
    if (next < minMonthDate || next > maxMonthDate) return;
    setCursor(next);
  };

  const todayISO = toISODate(today);

  const legend = [
    { color: "bg-emerald-500", label: "Approved" },
    { color: "bg-amber-400",   label: "Pending"  },
    { color: "bg-red-500",     label: "Rejected" },
    { color: "bg-purple-500",  label: "Holiday"  },
  ];

  // My recent decisions (approved/rejected) — soft-red list for rejected in this month
  const rejectedThisMonth = leaves.filter((l) => l.status === "rejected");
  const approvedThisMonth = leaves.filter((l) => l.status === "approved");
  const pendingThisMonth  = leaves.filter((l) => l.status === "pending");

  return (
    <div className={embedded ? "space-y-5" : "max-w-[1400px] mx-auto p-6 space-y-5"}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        {!embedded ? (
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900">Attendance</h1>
            <p className="text-sm text-slate-500 mt-0.5">Mark login/logout, apply leaves, mark holidays. Working days: Mon–Fri.</p>
          </div>
        ) : (
          <div className="text-[11px] text-slate-500">
            Working days: Mon–Fri · click any day to log in/out, apply leaves or mark a holiday.
          </div>
        )}
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} disabled={!canGoPrev} className={`p-2 rounded-md border ${canGoPrev ? "border-slate-200 hover:bg-slate-100 text-slate-700" : "border-slate-100 text-slate-300 cursor-not-allowed"}`} data-testid="att-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="text-sm font-semibold text-slate-800 min-w-[140px] text-center" data-testid="att-month-label">
            {monthLabel(cursor.getFullYear(), cursor.getMonth())}
          </div>
          <button onClick={() => shift(1)} disabled={!canGoNext} className={`p-2 rounded-md border ${canGoNext ? "border-slate-200 hover:bg-slate-100 text-slate-700" : "border-slate-100 text-slate-300 cursor-not-allowed"}`} data-testid="att-next-month">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[11px] text-slate-600 flex-wrap">
        {legend.map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${l.color}`} /> {l.label}</span>
        ))}
      </div>

      {/* Rejected soft-red banner */}
      {rejectedThisMonth.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3" data-testid="att-rejected-banner">
          <div className="text-sm font-semibold text-red-800 flex items-center gap-2"><XCircle className="h-4 w-4" /> {rejectedThisMonth.length} leave request{rejectedThisMonth.length === 1 ? "" : "s"} rejected this month</div>
          <ul className="mt-2 text-xs text-red-800 space-y-1">
            {rejectedThisMonth.map((lv) => (
              <li key={lv.id} className="flex items-center justify-between gap-2">
                <span>
                  <span className="font-semibold">{LEAVE_LABELS[lv.leave_type]}</span> — {lv.date}
                  {lv.decision_note ? <em className="text-red-600"> · “{lv.decision_note}”</em> : null}
                </span>
                <span className="text-[10px] text-red-500">By {lv.decided_by_name || lv.approver_name || "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {approvedThisMonth.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="att-approved-banner">
          <div className="text-sm font-semibold text-emerald-800 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> {approvedThisMonth.length} leave request{approvedThisMonth.length === 1 ? "" : "s"} approved this month</div>
          <ul className="mt-2 text-xs text-emerald-800 space-y-1">
            {approvedThisMonth.map((lv) => (
              <li key={lv.id} className="flex items-center justify-between gap-2" data-testid={`att-approved-row-${lv.id}`}>
                <span>
                  <span className="font-semibold">{LEAVE_LABELS[lv.leave_type]}</span> — {lv.date}
                  {lv.decided_by_name ? <em className="text-emerald-600"> · by {lv.decided_by_name}</em> : null}
                </span>
                <button
                  onClick={() => cancelLeave(lv.id)}
                  data-testid={`att-cancel-${lv.id}`}
                  className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-300 px-2 py-0.5 rounded transition"
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pendingThisMonth.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="att-pending-banner">
          <div className="text-sm font-semibold text-amber-800 flex items-center gap-2"><Clock3 className="h-4 w-4" /> {pendingThisMonth.length} pending leave request{pendingThisMonth.length === 1 ? "" : "s"} awaiting approval</div>
          <ul className="mt-2 text-xs text-amber-800 space-y-1">
            {pendingThisMonth.map((lv) => (
              <li key={lv.id} className="flex items-center justify-between gap-2" data-testid={`att-pending-row-${lv.id}`}>
                <span>
                  <span className="font-semibold">{LEAVE_LABELS[lv.leave_type]}</span> — {lv.date}
                  {lv.approver_name ? <em className="text-amber-600"> · awaiting {lv.approver_name}</em> : null}
                </span>
                <button
                  onClick={() => cancelLeave(lv.id)}
                  data-testid={`att-cancel-${lv.id}`}
                  className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 hover:text-white hover:bg-amber-600 border border-amber-300 px-2 py-0.5 rounded transition"
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Approval Inbox (for hierarchy leaders) */}
      <ApprovalInbox items={inbox} users={[]} onDecide={decide} />

      {/* Calendar grid */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="grid grid-cols-7 gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {WEEKDAY_HEADS.map((d) => <div key={d} className="px-1">{d}</div>)}
        </div>
        {loading ? (
          <CalendarGridSkeleton rows={5} />
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {grid.flat().map((cell) => {
              const iso = toISODate(cell.date);
              const isFuture = cell.date > today;
              return (
                <DayCell
                  key={iso + (cell.outside ? "-o" : "")}
                  date={cell.date}
                  outside={cell.outside}
                  entry={entryByDate[iso]}
                  leaves={leavesByDate[iso] || []}
                  isToday={iso === todayISO}
                  isFuture={isFuture}
                  onOpenAction={(d) => setDialogDate(d)}
                />
              );
            })}
          </div>
        )}
      </div>

      <ActionDialog
        open={!!dialogDate}
        onClose={() => setDialogDate(null)}
        date={dialogDate}
        isFuture={dialogDate ? dialogDate > today : false}
        onSubmit={submitAction}
      />

      {/* My Payout — this month. Hidden in embedded mode because the parent
          Profile page already renders the payslip card + payroll link. */}
      {!embedded && (
        <div className="pt-4" data-testid="att-my-payout">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="font-display text-lg font-semibold text-slate-800">My Payout — {monthLabel(cursor.getFullYear(), cursor.getMonth())}</h2>
          </div>
          <Payroll embedded forceUserId={user?.id} />
        </div>
      )}
    </div>
  );
}
